import {doppeActionTypeDefineMetadata} from '../../client-server-common/doppe-action-types/doppe-action-type';
import {
    DoppeActionCTAType,
    doppeActionCtaButtonsResetSettingsForLock,
    DoppeLinkSpecEntityType,
    DoppeLinkSpecType
} from '../../client-server-common/types/doppe-action-cta-button';
import type {PartiallyOptional} from '@wix/devzai-utils-common';
import {
    arrayFlatten,
    arrayRemoveNullValues,
    arraySkipTake,
    asArray,
    hideableValueCreateHidden,
    hideableValueGetVisibleValue,
    hideableValueIsVisibleAndNotEqualValue,
    immerProduce,
    iterableMapToArray
} from '@wix/devzai-utils-common';
import type {WixMediaResource} from '@wix/devzai-common-wix';
import {wixMediaResourceReplaceExternalImage} from '@wix/devzai-common-wix';
import {
    BymoPagePropsContentSettingSemanticMeaning,
    BymoPagePropsContentSettingType,
    bymoPagePropsCreateContentSettingMetadata,
    BymoPagePropsSpecialContentSettingMetadata,
    DoppeActionLayout,
    DoppeBusinessEntity,
    DoppeListDataItemsSource,
    doppeBusinessEntityDtoIsNotFound,
    DoppeExternalEventProvider,
    doppeExternalEventProviderResolveBusinessEntityReference,
    doppeListActionContentSettingsMetadataCreateForListActionWithMainDetails,
    doppeListActionGetDefaultListSettings,
    doppeListActionGetDefaultSearchListSettings,
    doppeListActionGetItemClickBehaviourDefaultListSettings,
    doppeListActionGetMainDetailsDefaultSettings,
    doppeListActionGetWithExternalDataSourceDefaultListSettings,
    doppeListActionItemCtaButtonsResetSettingsForLock,
    doppeListActionListItemCalculateItemButtons,
    doppeListActionListItemGetDefaultListItemSettingsMigrationWithOverrides,
    doppeListActionListItemSettingsMigrateBaseSettings,
    doppeListActionListItemWithCtaButtonsMigration,
    doppeListActionListItemWithItemClickBehaviourMigration,
    doppeListActionListItemWithSectionsMigration,
    DoppeListActionSettings,
    doppeListActionSettingsFilterHiddenListItemsFromActionView,
    doppeListActionSettingsGetListItemItemClickBehaviourDefaultSettings,
    doppeListActionSettingsGetListItemCtaButtonsDefaultSettings,
    doppeListActionSettingsGetListItemSectionsDefaultSettings,
    doppeListActionTemplateCtaButtonsSettingsDefaultValuesMigration,
    doppeListActionTemplateSectionsSettingsDefaultValuesMigration,
    doppeListActionWithMainDetailsGetMainImage,
    doppeListActionWithMainDetailsReplaceMainImage,
    doppeListActionContentSettingsMetadataCreateForListActionCommonSettings,
    BymoPagePropsContentSettingSpecialSemanticMeaning
} from '../../client-server-common';
import type {DoppeDtoEvent} from '../../client-server-common/types/doppe-dto-event';
import type {DoppeEventsExternalDataSource} from '../../client-server-common/types/doppe-events-external-data-source';
import {
    DoppeEventsExternalDataSourceType,
    doppeEventsExternalDataSourceResolveBusinessEntityReferences
} from '../../client-server-common/types/doppe-events-external-data-source';
import type {
    DoppeActionContentSectionsSettings
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import {
    doppeActionContentSectionSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-content-sections-settings';
import type {DoppeActionCtaButtonsSettings} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeActionCtaButtonsSettingsDefaultValuesMigration
} from '../../client-server-common/types/doppe-action-cta-buttons-settings';
import {
    doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction
} from '../../client-server-common/types/doppe-dto-action';
import type {DoppeActionTypeHoppEvent} from '../doppe-action-type-hopp-event/doppe-action-type-hopp-event';
import {convertDoppeDtoEventToHoppEventSettings} from '../doppe-action-type-hopp-event/doppe-action-type-hopp-event';
import {
    doppeActionGetMediaViewDefaultSettingsMigration
} from '../../client-server-common/types/doppe-action-media-view-settings';
import {
    doppeActionCtaButtonsResolveMainURL
} from '../doppe-actions-common/doppe-cta-button-utils/doppe-cta-button-utils';
import {
    bymoPageContextPropsResolveBusinessEntity,
    bymoPageContextPropsResolveBusinessEntityListItems
} from '../../doppe-sdk/bymo-page/bymo-page-context-props';

export namespace DoppeActionTypeHoppEvents {

    export interface EventSettings extends
        DoppeListActionSettings.ListItemBaseSettings,
        DoppeListActionSettings.ListItemWithCtaButtons,
        DoppeListActionSettings.ListItemWithSections,
        DoppeListActionSettings.ListItemWithItemClickBehaviour,
        DoppeActionTypeHoppEvent.BaseActionSettings {
    }

    export interface ActionSettings extends
        DoppeListActionSettings<EventSettings>,
        DoppeActionContentSectionsSettings,
        DoppeActionCtaButtonsSettings,
        DoppeListActionSettings.WithSearchSettings,
        DoppeListActionSettings.WithMainDetails,
        DoppeListActionSettings.WithItemClickBehaviour,
        DoppeListActionSettings.WithExternalDataSourceSupport<DoppeEventsExternalDataSource> {
        previewShowTitle: boolean;
        previewShowDescription: boolean;
        previewShowPrice: boolean;
        previewShowCoupon: boolean;
    }
}

export const doppeActionTypeHoppEvents = doppeActionTypeDefineMetadata<DoppeActionTypeHoppEvents.ActionSettings>({
    id: 'io.bymo.action/hopp-events',
    prepareForViewer: (actionSettings, options) => {
        doppeDtoActionSettingsRemoveInnerCtaContentForLockedAction(actionSettings, actionSettings => {
            doppeListActionItemCtaButtonsResetSettingsForLock(actionSettings);
            doppeActionCtaButtonsResetSettingsForLock(actionSettings);
        }, options);
    },
    resolveActionSettings: action => {

        const {
            listDataItems = [],
            previewShowTitle = true,
            previewShowDescription = true,
            previewShowPrice = true,
            previewShowCoupon = true,
            layout = DoppeActionLayout.Column,
            ...restSettings
        } = action.settings;

        return {
            ...restSettings,
            previewShowTitle: previewShowTitle,
            previewShowDescription: previewShowDescription,
            previewShowPrice: previewShowPrice,
            previewShowCoupon: previewShowCoupon,
            ...doppeListActionGetWithExternalDataSourceDefaultListSettings(action.settings),
            ...doppeListActionGetItemClickBehaviourDefaultListSettings(action.settings),
            ...doppeListActionGetMainDetailsDefaultSettings({}, action.settings),
            ...doppeListActionGetDefaultSearchListSettings(action.settings),
            ...doppeListActionGetDefaultListSettings({
                ...action.settings,
                layout: layout,
                listDataItems: listDataItems.map(event => {

                    const {
                        ctaButtonsOverridesMap,
                        customCtaButtons,
                        id,
                        deleted,
                        displayInSearch,
                        enabled,
                        sectionsOverridesMap,
                        customSections,
                        ...restEvent
                    } = event;

                    return {
                        ...doppeActionGetMediaViewDefaultSettingsMigration(event),
                        ...doppeListActionListItemWithCtaButtonsMigration(event, false),
                        ...doppeListActionListItemWithSectionsMigration(event),
                        ...doppeListActionListItemWithItemClickBehaviourMigration(event),
                        ...doppeListActionListItemGetDefaultListItemSettingsMigrationWithOverrides(event.id, event),
                        ...restEvent as PartiallyOptional<DoppeActionTypeHoppEvents.EventSettings, 'mainMediaViewSettings' | 'mainMediaViewLayout'  | 'ctaButtonsOverridesMap' |'customCtaButtons' | 'sectionsOverridesMap' | 'customSections'>
                    }
                }),
            }),
            ...doppeActionContentSectionSettingsDefaultValuesMigration(action.settings),
            ...doppeActionCtaButtonsSettingsDefaultValuesMigration(action.settings, false),
            ...doppeListActionTemplateCtaButtonsSettingsDefaultValuesMigration(action.settings, false),
            ...doppeListActionTemplateSectionsSettingsDefaultValuesMigration(action.settings),
        } satisfies DoppeActionTypeHoppEvents.ActionSettings
    },
    actionSettingsMetadata: {
        contentSettingsMetadata: {
            ...doppeListActionContentSettingsMetadataCreateForListActionWithMainDetails(),
            ...doppeListActionContentSettingsMetadataCreateForListActionCommonSettings(),
            previewShowTitle: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.previewShowTitle'}),
            previewShowDescription: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.previewShowDescription'}),
            previewShowPrice: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.previewShowPrice'}),
            previewShowCoupon: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.previewShowCoupon'}),
            externalDataSourceItemsCount: BymoPagePropsSpecialContentSettingMetadata.Ignored,
        },
        supportsActionCtaButtons: {
            customCtaButtonTypes: {
                [DoppeActionCTAType.Register]: true
            }
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        },
        hideableContentSettingsNames: {
                mainImage: true,
                mainTitle: true
            },
        bindableContentSettingsNames: {}
    },
    actionListItemSettingsMetadata: {
        contentSettingsMetadata: {
            eventTitle: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
            description: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.RichText, BymoPagePropsContentSettingSemanticMeaning.EntityDescription),
            eventImages: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.MultipleMedia, BymoPagePropsContentSettingSemanticMeaning.EntityMedia),
            location: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
            ribbon: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityRibbon),
            eventPageLink: BymoPagePropsSpecialContentSettingMetadata.Ignored,
            eventStartDate: BymoPagePropsSpecialContentSettingMetadata.Ignored,
            eventEndDate: BymoPagePropsSpecialContentSettingMetadata.Ignored,
            priceData: BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered,
            couponCode: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityCouponCode),
            isDateTBD: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.isDateTBD'}),
        },
        supportsActionCtaButtons: {
            customCtaButtonTypes: {
                [DoppeActionCTAType.Register]: true
            }
        },
        supportsContentSections: {
            customContentSectionsTypes: {}
        },
        hideableContentSettingsNames: {
                couponCode: true,
                description: true,
                eventStartDate: true,
                eventEndDate: true,
                eventTitle: true,
                itemClickBehaviour: true,
                location: true,
                priceData: true,
                ribbon: true,
                timeZoneId: true
            },
        bindableContentSettingsNames: {}
    },
    isWidgetOpener: true,
    canRenderInPage: true,
    skipReportingOnGoalAchievedOnActionButtonClick: true,
    isListItemLinkOpener: true,
    supportsListLayout: true,
    resolveUsedMedia: actionSettings => {
        return arrayRemoveNullValues([...arrayFlatten(iterableMapToArray(actionSettings.listDataItems, (event, skip) => {
            const mediaItems = arrayRemoveNullValues(asArray(hideableValueGetVisibleValue(event.eventImages, []) ?? []));
            return mediaItems.length > 0 ? mediaItems : skip;
        })), hideableValueIsVisibleAndNotEqualValue(actionSettings.mainImage, null) ? actionSettings.mainImage : null]);
    },
    resolveMainMedia: actionSettings => {
        const event = actionSettings.listDataItems[0];
        const mediaItems = event ? arrayRemoveNullValues(asArray(hideableValueGetVisibleValue(event.eventImages, []) ?? [])) : [];
        return doppeListActionWithMainDetailsGetMainImage(actionSettings, mediaItems[0]);
    },
    resolveMainLink: (actionSettings, viewerLang, bymoPageProps, viewerPagesRoutesManager, bymoPageContextProps) => {
        return doppeActionCtaButtonsResolveMainURL(actionSettings, {
            sourceLinkSpec: null,
            viewerLang: viewerLang,
            bymoPageProps: bymoPageProps,
            viewerPagesRoutesManager: viewerPagesRoutesManager,
            bymoPageContextProps: bymoPageContextProps
        }) ?? null;
    },
    isConnectedToExternalDataSource: actionSettings => {
        return !!actionSettings.externalDataSource
    },
    resolveActualActionSettings: (actionSettings, bymoPageContextProps) => {
        const {externalDataSource, externalDataSourceItemsCount} = actionSettings;
        if (externalDataSource !== null) {
            const businessEntityRef = doppeEventsExternalDataSourceResolveBusinessEntityReferences(externalDataSource);
            const externalEvents = bymoPageContextPropsResolveBusinessEntityListItems(bymoPageContextProps, businessEntityRef);

            const isHoppEvents = externalDataSource.type === DoppeEventsExternalDataSourceType.UpcomingAndOldHoppEvents ||
                externalDataSource.type === DoppeEventsExternalDataSourceType.UpcomingHoppEvents ||
                externalDataSource.type === DoppeEventsExternalDataSourceType.OldHoppEvents ||
                externalDataSource.type === DoppeEventsExternalDataSourceType.HoppEventsByCategories;

            const listDataItems = arraySkipTake(externalEvents, 0, externalDataSourceItemsCount).map(doppeDtoEvent => {
                return convertDoppeDtoEventToHoppEventsSettings(doppeDtoEvent, isHoppEvents ? DoppeExternalEventProvider.Hopp : DoppeExternalEventProvider.Wix);
            });

            return {
                ...actionSettings,
                listDataItems: listDataItems
            };
        }
        return actionSettings;
    },
    resolveActualListItemSettings: (listItemId, actionSettings, bymoPageContextProps) => {
        const listItem = actionSettings.listDataItems.find(item => item.id === listItemId);
        if (!listItem) return undefined;

        const {eventProvider, eventProviderId} = listItem;
        if (eventProvider === null || eventProviderId === null) return listItem;

        const boundEvent = bymoPageContextPropsResolveBusinessEntity(
            bymoPageContextProps,
            doppeExternalEventProviderResolveBusinessEntityReference(eventProvider, eventProviderId)
        );

        if (!boundEvent || doppeBusinessEntityDtoIsNotFound(boundEvent)) return undefined;

        return {
            ...listItem,
            ...convertDoppeDtoEventToHoppEventsSettings(boundEvent as DoppeDtoEvent, eventProvider),
        };
    },
    resolveUsedBusinessEntities: actionSettings => {
        const externalDataSource = actionSettings.externalDataSource;
        if (externalDataSource) {
            return [
                doppeEventsExternalDataSourceResolveBusinessEntityReferences(externalDataSource)
            ];
        } else {
            return iterableMapToArray(actionSettings.listDataItems, (event, skip) => {
                return event.eventProvider !== null && event.eventProviderId !== null ?
                    doppeExternalEventProviderResolveBusinessEntityReference(
                        event.eventProvider,
                        event.eventProviderId
                    ) : skip
            });
        }
    },
    resolveListDataItemsSource: actionSettings => {
        const {externalDataSource} = actionSettings;
        return externalDataSource !== null
            ? {type: DoppeListDataItemsSource.Dynamic, source: doppeEventsExternalDataSourceResolveBusinessEntityReferences(externalDataSource)}
            : {type: DoppeListDataItemsSource.Static};
    },
    getSupportedDataSources: ({hasCreatorsMetaSiteTpaInstalled}) => {
        return hasCreatorsMetaSiteTpaInstalled ?
            [
                {type: DoppeEventsExternalDataSourceType.UpcomingHoppEvents},
                {type: DoppeEventsExternalDataSourceType.OldHoppEvents},
                {type: DoppeEventsExternalDataSourceType.UpcomingAndOldHoppEvents},
                {type: DoppeEventsExternalDataSourceType.HoppEventsByCategories, categoryIds: [] as string[]},
            ] :
            [];
    },
    resolveListItemEntityBinding: (listItemSettings, _actionSettings) => {
        const {eventProvider, eventProviderId} = listItemSettings;
        return eventProvider !== null && eventProviderId !== null
            ? doppeExternalEventProviderResolveBusinessEntityReference(eventProvider, eventProviderId)
            : null;
    },
    resolveListItemSettingsFromBoundEntityReference: (ref) => {
        switch (ref.businessEntity) {
            case DoppeBusinessEntity.WixEvent:
                return {eventProvider: DoppeExternalEventProvider.Wix, eventProviderId: ref.id};
            case DoppeBusinessEntity.HoppEvent:
                return {eventProvider: DoppeExternalEventProvider.Hopp, eventProviderId: ref.id};
            default:
                return {};
        }
    },
    resolveListItemsCount: actionSettings => {
        return actionSettings.externalDataSource ? actionSettings.externalDataSourceItemsCount : doppeListActionSettingsFilterHiddenListItemsFromActionView(actionSettings.listDataItems).length;
    },
    resolveListItems: (action, viewerLang, bymoPageProps, viewerPagesRoutesManager, bymoPageContextProps) => {
        const actionSettings = action.settings;
        return actionSettings.listDataItems.map(item => {
            // Try to resolve the event entity from business entities data
            const eventEntity = item.eventProviderId ? bymoPageContextPropsResolveBusinessEntity(
                bymoPageContextProps,
                {businessEntity: DoppeBusinessEntity.HoppEvent, id: item.eventProviderId}
            ) : undefined;

            // Check if we have valid entity data
            const hasValidEntity = eventEntity && !doppeBusinessEntityDtoIsNotFound(eventEntity);

            // Resolve values from entity or fall back to stored settings
            const title = hasValidEntity
                ? (eventEntity.name ?? hideableValueGetVisibleValue(item.eventTitle, ''))
                : hideableValueGetVisibleValue(item.eventTitle, '');

            const description = hasValidEntity
                ? (eventEntity.description ?? hideableValueGetVisibleValue(item.description, ''))
                : hideableValueGetVisibleValue(item.description, '');

            const ribbon = hideableValueGetVisibleValue(item.ribbon, '');

            // Resolve image from entity or fall back to stored settings
            // WixImageInfo is a valid WixImageResource which is a WixMediaResource
            const entityImages = hasValidEntity && eventEntity.media
                ? (eventEntity.media as WixMediaResource[])
                : null;
            const storedImages = arrayRemoveNullValues(asArray(hideableValueGetVisibleValue(item.eventImages, []) ?? []));
            const image = (entityImages && entityImages.length > 0 ? entityImages[0] : storedImages[0]) ?? null;

            // Calculate URL from link spec
            const url = doppeActionCtaButtonsResolveMainURL(
                doppeListActionListItemCalculateItemButtons(actionSettings.listItemTemplateCtaButtonsSettings, item),
                {
                    sourceLinkSpec: item.eventProviderId ? {
                        type: DoppeLinkSpecType.Entity,
                        entityType: DoppeLinkSpecEntityType.Event,
                        entityId: item.eventProviderId
                    } : null,
                    viewerLang: viewerLang,
                    bymoPageProps: bymoPageProps,
                    viewerPagesRoutesManager: viewerPagesRoutesManager,
                    bymoPageContextProps: bymoPageContextProps
                }
            ) ?? null;

            return {
                title: title,
                action: action,
                couponCode: item.couponCode ?? hideableValueCreateHidden(''),
                description: hideableValueCreateHidden(description),
                image: image,
                itemId: item.id,
                ribbon: hideableValueCreateHidden(ribbon),
                url: url,
                enabled: item.enabled,
                deleted: item.deleted,
                displayInSearch: item.displayInSearch,
            }
        });
    },
    resolveListSearchSettings: actionSettings => {
        return actionSettings.listSearchSettings;
    },
    replaceExternalImages: (actionSettings, externalImagesUrlToImageResourceMap) => {
        return immerProduce(actionSettings, actionSettings => {
            for (const item of actionSettings.listDataItems) {
                if (item.eventImages) {
                    const images = item.eventImages ?? [];
                    item.eventImages = images.map(image => {
                        return wixMediaResourceReplaceExternalImage(image, externalImagesUrlToImageResourceMap)
                    })
                }
            }

            doppeListActionWithMainDetailsReplaceMainImage(actionSettings, externalImagesUrlToImageResourceMap);
        })
    }
});

export function convertDoppeDtoEventToHoppEventsSettings(event: DoppeDtoEvent, eventProvider?: DoppeExternalEventProvider): DoppeActionTypeHoppEvents.EventSettings {
    const singleEventSettings = convertDoppeDtoEventToHoppEventSettings({
        event: event,
        eventProvider: eventProvider,
    });

    return {
        ...singleEventSettings,
        ...doppeListActionListItemSettingsMigrateBaseSettings(event),
        ...doppeListActionSettingsGetListItemCtaButtonsDefaultSettings(),
        ...doppeListActionSettingsGetListItemSectionsDefaultSettings(),
        ...doppeListActionSettingsGetListItemItemClickBehaviourDefaultSettings(),
    } satisfies DoppeActionTypeHoppEvents.EventSettings;
} 